import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useContext,
} from 'react';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { currencies } from '@travelity/api';
import { NavigateOptions, UNSAFE_NavigationContext } from 'react-router-dom';

export function useExpand<T>(): [T | null, (v: T | null) => void] {
    const [expanded, setExpanded] = useState<T | null>(null);
    const toggleExpand = useCallback((id: T | null) => {
        setExpanded(prev => (prev === id ? null : id));
    }, []);
    return [expanded, toggleExpand];
}

export function useChange<T>(
    initialValue: T | null = null
): [T | null, (e: any, v: T | null) => void] {
    const [value, setValue] = useState<T | null>(initialValue);
    const changeValue = useCallback((e: any, newValue: T | null) => {
        setValue(newValue);
    }, []);

    return [value, changeValue];
}

export function useResizeRef<T extends HTMLElement = HTMLDivElement>(
    callback: (w: number, h: number) => void,
    debounce = 0
): RefObject<T> {
    const elRef = useRef<T>(null);

    const callbackWithDebounce = useCallback(
        debounce ? _debounce(callback, debounce) : callback,
        [callback, debounce]
    );

    useEffect(() => {
        if (!elRef.current) return () => null;
        const el = elRef.current;

        const resizeObserver = new ResizeObserver(entries => {
            // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
            // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) return;
                // eslint-disable-next-line no-restricted-syntax
                for (const entry of entries) {
                    callbackWithDebounce(
                        entry.contentRect.width,
                        entry.contentRect.height
                    );
                }
            });
        });

        resizeObserver.observe(el);

        return () => resizeObserver.unobserve(el);
    }, [callbackWithDebounce, debounce]);

    return elRef;
}

export const useSelectOptions = <
    T extends object = Record<string, any>,
    ExtendsSelf extends boolean = false
>(
    options: T[] | undefined,
    labelKey: string,
    valueKey: string,
    extendSelf: ExtendsSelf
): ExtendsSelf extends false
    ? { label: string; value: string }[]
    : (T & { label: string; value: string })[] => {
    return useMemo(
        () =>
            options?.map(option => ({
                ...(extendSelf && option),
                label: _get(option, labelKey || 'name') || '',
                value: _get(option, valueKey || 'id') || '',
            })) || [],
        [options, labelKey, valueKey]
    );
};

export const useDynamicState = <T>(
    value: T
): [state: T, setState: Dispatch<SetStateAction<T>>] => {
    const [state, setState] = useState<T>(value);
    useEffect(() => {
        if (value && value !== state) {
            setState(value);
        }
    }, [value]);

    return [state, setState];
};

export const useIsChanged = (value: any, skipInitial?: boolean): boolean => {
    const ref = useRef(skipInitial ? value : undefined);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current !== value;
};

export const useLog = (name = 'Component', extraParams: any[] = []) => {
    // eslint-disable-next-line no-console
    console.log(`${name} rendered`, ...extraParams);
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log(`${name} mounted`);
        return () => {
            // eslint-disable-next-line no-console
            console.log(`${name} unmounted`);
        };
    }, []);

    useEffect(() => {
        if (extraParams.length > 0) {
            // eslint-disable-next-line no-console
            console.log(`${name} params changed`, ...extraParams);
        }
    }, [...extraParams]);
};

export const useCurrencyOptions = () => {
    return useMemo(
        () =>
            currencies.map(c => ({
                label: `${c.abbr} - ${c.name}`,
                value: c.abbr,
            })),
        []
    );
};

export const useLoadOnScroll = (
    {
        hasNextPage,
        fetchNextPage,
    }: {
        hasNextPage: boolean;
        fetchNextPage: () => void;
    },
    offset: number = 180
) => {
    const loadMoreRef = useRef(hasNextPage);
    loadMoreRef.current = hasNextPage;
    const onScroll = useCallback(
        (container: HTMLElement) => {
            if (
                container.scrollHeight -
                    container.scrollTop -
                    container.clientHeight <
                    offset &&
                loadMoreRef.current
            )
                fetchNextPage();
        },
        [fetchNextPage]
    );

    return { onScroll };
};

export const joinPaths = (paths: string[]): string =>
    paths.join('/').replace(/\/\/+/g, '/');

export const useNavigateAbsolute = () => {
    const { basename, navigator } = useContext(UNSAFE_NavigationContext);

    const activeRef = useRef(false);

    useEffect(() => {
        activeRef.current = true;
    }, []);

    return useCallback(
        (to: string | number, options: NavigateOptions = {}) => {
            if (!activeRef.current) return;

            if (typeof to === 'number') {
                navigator.go(to);
                return;
            }

            const path = {
                pathname: to,
            };

            if (basename !== '/') {
                path.pathname =
                    path.pathname === '/'
                        ? basename
                        : joinPaths([basename, path.pathname]);
            }

            (options.replace ? navigator.replace : navigator.push)(
                path,
                options.state,
                options
            );
        },
        [basename, navigator]
    );
};

export const useLocalStorage = <T>(
    key: string,
    defaultValue: T
): [T, (value: T) => void] => {
    const [value, setValue] = useState<T>(() => {
        const storedValue = localStorage.getItem(key);
        return storedValue ? JSON.parse(storedValue) : defaultValue;
    });

    const setStoredValue = (newValue: T) => {
        setValue(newValue);
        localStorage.setItem(key, JSON.stringify(newValue));
    };

    useEffect(() => {
        const storedValue = localStorage.getItem(key);
        if (storedValue) {
            const parsedValue = JSON.parse(storedValue);
            setValue(parsedValue);
        }
    }, [key]);

    return [value, setStoredValue];
};
